import { useGetAllDashboardsInfo, useGetDashboard } from "api/api-hooks/dashboards-hooks";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeParams } from "primereact/dropdown";
import { TabPanel, TabView } from "primereact/tabview";
import React, { useState } from "react";
import useDashboardStore from "store/dashboard-store";
import { v4 as uuidv4 } from "uuid";
import { DashboardLayoutType, OldDashboardComponent } from "api/model/dashboard/DashboardComponent";

interface ImportDashboardProps {
  dashboard: Dashboard;
  isImportDashboardDialogVisible: boolean;
  setIsImportDashboardDialogVisibility: (isImportDashboardDialogVisible: boolean) => void;
  importNewDashboard: (dashboard: Dashboard) => void;
}

export const handleAddNewTab = (
  dashboard: Dashboard,
  setDashboard: any,
  componentsToAdd: OldDashboardComponent[],
  tabTitle?: string
) => {
  const updatedCurrentDashboard = {
    ...dashboard,
    dashboardTabsJs: [
      ...dashboard.dashboardTabsJs,
      {
        uuid: uuidv4(),
        title: tabTitle ?? `New Tab`,
        componentGroups: [
          {
            id: uuidv4(),
            layout: {
              type: DashboardLayoutType.HORIZONTAL_FLEX,
            },
            components: componentsToAdd,
          },
        ],
      },
    ],
  };
  setDashboard(updatedCurrentDashboard);
};

const ImportDashboardOrTab = (props: ImportDashboardProps) => {
  const setDashboard = useDashboardStore(state => state.setDashboard);
  const [selectedDashboardId, setSelectedDashboardId] = useState<number | null>(null);
  const [selectedDashboardTabId, setSelectedDashboardTabId] = useState<number | null>(null);
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const { data: dashboards } = useGetAllDashboardsInfo();
  const { data: dashboardData } = useGetDashboard(
    selectedDashboardId !== null ? selectedDashboardId : 0,
    {
      enabled: selectedDashboardId !== null,
    }
  );

  const dashboardOptions =
    dashboards?.map(dashboard => ({
      label: dashboard.title,
      value: dashboard.id,
    })) || [];

  const tabOptions =
    dashboardData?.dashboardTabs.map((tab, index) => ({
      label: tab.title,
      value: index,
    })) || [];

  const handleDashboardChange = (e: DropdownChangeParams) => {
    setSelectedDashboardId(e.value);
    setSelectedDashboardTabId(null);
  };

  const handleTabChange = (e: DropdownChangeParams) => {
    setSelectedDashboardTabId(e.value);
  };

  const handleImportDashboard = () => {
    if (dashboardData && selectedTabIndex === 0) {
      props.importNewDashboard(dashboardData);
    } else if (dashboardData && selectedTabIndex === 1 && selectedDashboardTabId !== null) {
      const selectedTab = dashboardData.dashboardTabs[selectedDashboardTabId];
      const newDashboard: Dashboard = {
        ...dashboardData,
        dashboardTabs: [selectedTab],
      };
      props.importNewDashboard(newDashboard);
    }
    props.setIsImportDashboardDialogVisibility(false);
    setSelectedDashboardId(null);
    setSelectedDashboardTabId(null);
  };

  const handleImportTab = () => {
    if (dashboardData && selectedTabIndex === 1 && selectedDashboardTabId !== null) {
      const selectedTab = dashboardData.dashboardTabs[selectedDashboardTabId];
      const componentsToAdd = selectedTab.components.map((component: OldDashboardComponent) => ({
        id: component.id,
        title: component.title,
        type: component.componentType,
        config: component.jsonConfig ? JSON.parse(component.jsonConfig) : {},
        layout: {
          HORIZONTAL_FLEX: {
            width: component.width || 12,
            height: "auto",
          },
        },
        TYPE: component.TYPE,
        width: component.width,
        componentType: component.componentType,
        jsonConfig: component.jsonConfig,
      }));

      handleAddNewTab(props.dashboard, setDashboard, componentsToAdd, selectedTab.title);
    }

    props.setIsImportDashboardDialogVisibility(false);
    setSelectedDashboardId(null);
    setSelectedDashboardTabId(null);
  };

  const handleTabChangeView = (e: any) => {
    setSelectedTabIndex(e.index);
    setSelectedDashboardId(null);
    setSelectedDashboardTabId(null);
  };

  const displayFooter = (
    <div>
      <Button
        label="Import"
        icon="pi pi-check"
        onClick={selectedTabIndex === 0 ? handleImportDashboard : handleImportTab}
        disabled={
          (selectedTabIndex === 0 && selectedDashboardId === null) ||
          (selectedTabIndex === 1 && selectedDashboardTabId === null)
        }
      />
    </div>
  );

  return (
    <Dialog
      header="Import"
      visible={props.isImportDashboardDialogVisible}
      onHide={() => props.setIsImportDashboardDialogVisibility(false)}
      footer={displayFooter}
      style={{ width: "25rem", maxHeight: "30rem" }}
      blockScroll
    >
      <TabView activeIndex={selectedTabIndex} onTabChange={handleTabChangeView}>
        <TabPanel header="Dashboard">
          <div className="w-full flex flex-column gap-3">
            <label htmlFor="dashboard">Select dashboard</label>
            <Dropdown
              id="dashboard"
              value={selectedDashboardId}
              options={dashboardOptions}
              filter
              onChange={handleDashboardChange}
              optionLabel="label"
              optionValue="value"
              placeholder="Select a dashboard"
            />
          </div>
        </TabPanel>
        <TabPanel header="Tab">
          <div className="w-full flex flex-column gap-3">
            <label htmlFor="dashboard">Select dashboard</label>
            <Dropdown
              id="dashboard"
              value={selectedDashboardId}
              options={dashboardOptions}
              filter
              onChange={handleDashboardChange}
              optionLabel="label"
              optionValue="value"
              placeholder="Select a dashboard"
            />
          </div>
          {selectedDashboardId !== null && (
            <div className="w-full flex flex-column gap-3 mt-3">
              <label htmlFor="tab">Select tab</label>
              <Dropdown
                id="tab"
                value={selectedDashboardTabId}
                options={tabOptions}
                filter
                onChange={handleTabChange}
                optionLabel="label"
                optionValue="value"
                placeholder="Select a tab"
                disabled={tabOptions.length === 0}
              />
            </div>
          )}
        </TabPanel>
      </TabView>
    </Dialog>
  );
};

export default ImportDashboardOrTab;
